import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/services/api'

export interface User {
  id: number
  email: string
  username?: string
  roles: string[]
  isVerified: boolean
  isActive: boolean
  createdAt: string
  lastLoginAt?: string
}

interface AuthState {
  user: User | null
  token: string | null
  // refreshToken n'est plus stocké ici, il est dans un cookie HTTP-only secure
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, username?: string) => Promise<void>
  logout: () => void
  refreshAuth: () => Promise<void>
  clearError: () => void
  fetchUserProfile: () => Promise<void>
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/login_check', { email, password })
          const { token, user } = response.data
          // Le refresh_token est automatiquement stocké dans un cookie HTTP-only par le backend
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          })
          
          // Set authorization header for future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Erreur de connexion',
            isLoading: false,
          })
          throw error
        }
      },

      register: async (email: string, password: string, username?: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/users', {
            email,
            password,
            username,
          })
          const { token, user } = response.data
          // Le refresh_token est automatiquement stocké dans un cookie HTTP-only par le backend
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          })
          
          // Set authorization header for future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Erreur lors de l\'inscription',
            isLoading: false,
          })
          throw error
        }
      },

      logout: async () => {
        try {
          // Appeler l'endpoint logout pour expirer les tokens côté serveur
          await api.post('/logout')
        } catch (error) {
          // Même si l'appel échoue, on continue avec le logout local
          console.warn('Erreur lors du logout serveur:', error)
        }
        
        // Vider le localStorage
        localStorage.removeItem('auth-storage')
        
        // Vider le sessionStorage pour forcer la re-vérification d'âge
        sessionStorage.removeItem('sessionId')
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
        delete api.defaults.headers.common['Authorization']
      },

      refreshAuth: async () => {
        try {
          // Appel à l'endpoint de refresh
          // Le refresh_token est envoyé automatiquement via le cookie HTTP-only
          const response = await api.post('/token/refresh', {})
          const { token } = response.data
          // Le nouveau refresh_token est automatiquement mis à jour dans le cookie par le backend
          
          set({
            token,
            isAuthenticated: true,
          })
          
          // Set authorization header for future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        } catch (error) {
          // Refresh failed, logout user
          console.error('Échec du rafraîchissement du token:', error)
          get().logout()
        }
      },


      clearError: () => set({ error: null }),

      fetchUserProfile: async () => {
        const { user } = get()
        if (!user?.id) return

        try {
          const response = await api.get(`/api/users/${user.id}`)
          const updatedUser = response.data
          
          set({
            user: updatedUser,
          })
        } catch (error) {
          console.warn('Erreur lors de la récupération du profil utilisateur:', error)
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // refreshToken n'est plus persisté ici, il est dans un cookie HTTP-only secure
      }),
    }
  )
)
